import {EstateType, OfferType, Disposition, Ownership, Construction, Equipped, ResultOrder} from '.cache/__types__';
import {ParsedUrlQuery} from 'querystring';
import {LocaleType} from 'src/types/general';
import {getLocalizedRoute, Route} from './routes';
import {listDefaultType as defaultOrder} from '@constants/Order';
import {NominatimOsmId} from '@liveComponents/Autocomplete/autocompleteHelper';

export const defaultLimit = 15;
export const defaultPage = 1;

export const asArray = <T extends string>(v: T | T[]) => (Array.isArray(v) ? v : (v.split('/') as T[]));

export const asValue = <T extends unknown>(v: T | T[]) => (Array.isArray(v) ? v[0] : v);

export const asNumber = <T extends string>(v: T | T[]) => parseInt(asValue(v));
export const asFloat = <T extends string>(v: T | T[]) => parseFloat(asValue(v));

export const parseQuery = (query: ParsedUrlQuery) => {
    const page = query.page ? asNumber(query.page) : defaultPage;

    return {
        offerType: query.offerType ? asArray(query.offerType as OfferType) : [],
        estateType: query.estateType ? asArray(query.estateType as EstateType) : [],
        disposition: query.disposition ? asArray(query.disposition as Disposition) : [],
        ownership: query.ownership ? asArray(query.ownership as Ownership) : [],
        construction: query.construction ? asArray(query.construction as Construction) : [],
        equipped: query.equipped ? asArray(query.equipped as Equipped) : [],
        priceFrom: query.priceFrom ? asNumber(query.priceFrom) : null,
        priceTo: query.priceTo ? asNumber(query.priceTo) : null,
        balcony: query.balcony === 'true',
        loggia: query.loggia === 'true',
        terrace: query.terrace === 'true',
        parking: query.parking === 'true',
        garage: query.garage === 'true',
        newBuilding: query.newBuilding === 'true',
        lift: query.lift === 'true',
        cellar: query.cellar === 'true',
        region: query.region ? asValue(query.region) : null,
        advertId: query.advertId ? asValue(query.advertId as string) : null,
        surfaceFrom: query.surfaceFrom ? asNumber(query.surfaceFrom) : null,
        surfaceTo: query.surfaceTo ? asNumber(query.surfaceTo) : null,
        order: query.order ? asValue(query.order as ResultOrder) : defaultOrder,
        regionOsmId: query.regionOsmId ? asValue(query.regionOsmId as NominatimOsmId) : null,
        osm_value: query.osm_value ? asValue(query.osm_value as string) : '',
        roommate: query.roommate === undefined || query.roommate === 'true',
        includeImports: query.includeImports === undefined || query.includeImports === 'true',
        watchdog: query.watchdog && !Number.isNaN(parseInt(query.watchdog as string)) ? asValue(query.watchdog as string) : null,

        offset: getOffset(defaultLimit, page),
        limit: defaultLimit,
        page: query.page ? asNumber(query.page) : defaultPage,
    };
};

export const getOffset = (limit: number, page: number): number => {
    const offset = page < 1 ? limit : (page - 1) * limit;

    return offset;
};

export type Filter = ReturnType<typeof parseQuery>;

const reduceFilter = (filter: Filter, pagination: boolean = false) => {
    const reducedFilter: Partial<Filter> = {...filter};

    for (const key in filter) {
        switch (key) {
            case 'estateType':
                const withoutUndefined = filter[key].filter((row) => row !== EstateType.Undefined);
                if (!withoutUndefined.length) {
                    delete reducedFilter[key];
                } else {
                    reducedFilter[key] = withoutUndefined;
                }
                break;
            case 'offerType':
            case 'disposition':
            case 'ownership':
            case 'construction':
            case 'equipped':
                if (!filter[key].length) {
                    delete reducedFilter[key];
                }
                break;
            case 'advertId':
            case 'surfaceFrom':
            case 'surfaceTo':
            case 'priceFrom':
            case 'priceTo':
            case 'region':
            case 'balcony':
            case 'loggia':
            case 'terrace':
            case 'parking':
            case 'garage':
            case 'newBuilding':
            case 'lift':
            case 'cellar':
            case 'order':
            case 'regionOsmId':
            case 'osm_value':
                if (!filter[key]) {
                    delete reducedFilter[key];
                }
                break;
            case 'roommate':
            case 'includeImports':
                if (filter[key]) {
                    delete reducedFilter[key];
                }
                break;
            case 'watchdog':
                if (filter[key] && !pagination) {
                    delete reducedFilter[key];
                } else if (!filter[key]) {
                    delete reducedFilter[key];
                }
                break;

            default:
                //nothing
                break;
        }
    }

    return reducedFilter;
};

const reduceFilterURL = (filter: Filter, noRange: boolean = false, pagination: boolean = false) => {
    const reducedFilter: Partial<Filter> = {...reduceFilter(filter, pagination)};

    for (const key in filter) {
        switch (key) {
            case 'priceFrom':
            case 'priceTo':
                if (noRange) {
                    delete reducedFilter.priceFrom;
                    delete reducedFilter.priceTo;
                }
                break;
            case 'offset':
            case 'limit':
                delete reducedFilter[key];
                break;

            default:
                //nothing
                break;
        }
    }

    return reducedFilter;
};

export const reduceFilterDB = (filter: Filter) => {
    const reducedFilter: Partial<Filter> = {...reduceFilter(filter)};

    for (const key in filter) {
        switch (key) {
            case 'page':
            case 'osm_value':
                delete reducedFilter[key];
                break;

            default:
                //nothing
                break;
        }
    }

    return reducedFilter;
};

export const getSearchReducedURL = (route: Route, locale: LocaleType, filter: Filter) => (key: string, value?: string) => {
    const reducedFilter: Partial<Filter> = {...reduceFilterURL(filter), page: 1};
    switch (key) {
        case 'offerType':
        case 'estateType':
        case 'advertId':
        case 'surfaceFrom':
        case 'surfaceTo':
        case 'priceFrom':
        case 'priceTo':
        case 'region':
        case 'balcony':
        case 'loggia':
        case 'terrace':
        case 'parking':
        case 'garage':
        case 'newBuilding':
        case 'lift':
        case 'cellar':
        case 'order':
        case 'regionOsmId':
        case 'osm_value':
            delete reducedFilter[key];
            break;
        case 'disposition':
        case 'ownership':
        case 'construction':
        case 'equipped':
            const index = reducedFilter[key]?.findIndex((row) => row === value);
            if (index !== undefined && index !== -1) {
                reducedFilter[key]?.splice(index, 1);
            }
    }

    return getLocalizedRoute(route, locale, reducedFilter);
};

export const getSearchURL =
    (route: Route, locale: LocaleType, noRange: boolean = false, pagination: boolean = false) =>
    (filter: Filter) =>
        getLocalizedRoute(route, locale, reduceFilterURL(filter, noRange, pagination));
